import React, { useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './styles/template.css';
import './styles/edit-mode.css';
import './styles/auth.css';
import { AuthProvider, useAuth } from './context/AuthContext.tsx';
import Login from './components/Login.tsx';
import Products from './components/Products.tsx';
import { useContentManager } from './hooks/useContentManager.ts';
import { useAssetsReady } from './hooks/useAssetsReady.ts';
import { useScrewAnimation } from './hooks/useScrewAnimation.ts';
import { useScrollReveal } from './hooks/useScrollReveal.ts';
import SiteHeader from './components/layout/SiteHeader.tsx';
import LoadingOverlay from './components/layout/LoadingOverlay.tsx';
import ScrollProgress from './components/layout/ScrollProgress.tsx';
import SectionDots from './components/layout/SectionDots.tsx';
import ThermalStage from './components/home/ThermalStage.tsx';
import HeroSection from './components/home/HeroSection.tsx';
import ProductsIntroSection from './components/home/ProductsIntroSection.tsx';
import OrderingSection from './components/home/OrderingSection.tsx';
import ContactSection from './components/home/ContactSection.tsx';

const MainContent: React.FC = () => {
  const { isAuthenticated, isAdmin, user, logout } = useAuth();
  const {
    isEditMode,
    isLoading,
    dirtyFields,
    getContent,
    handleContentSave,
    handleDirtyStateChange,
    handleEditModeToggle,
  } = useContentManager();
  // Keep the loading modal up until the DB content arrives and the images it
  // references have loaded too
  const showLoading = useAssetsReady(isLoading, [
    getContent('company-logo'),
    getContent('hero-screw-image'),
    getContent('hero-sparks-image'),
  ]);

  // Cross-page hash links (e.g. /#contact from the catalog) full-load this
  // route; the browser's own fragment scroll runs before React has rendered
  // the sections, so re-apply it once the page is ready.
  useEffect(() => {
    if (showLoading) return;
    const hash = window.location.hash;
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) requestAnimationFrame(() => el.scrollIntoView());
  }, [showLoading]);

  const stageRef = useRef<HTMLDivElement>(null);
  const screwRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLElement>(null);

  // Edit mode freezes the screw (static dock) and clears the scroll reveals so
  // every editable field is visible and clickable.
  useScrewAnimation({ stageRef, screwRef, visualRef, enabled: !isEditMode });
  useScrollReveal({ mainRef, enabled: !isEditMode });

  const sectionProps = {
    isEditMode,
    getContent,
    onContentSave: handleContentSave,
    onDirtyStateChange: handleDirtyStateChange,
  };

  return (
    <div className={`App ${isEditMode ? 'edit-mode' : ''}`}>
      {showLoading && <LoadingOverlay />}
      <a className="skip-link" href="#main">跳至主要內容</a>
      <ScrollProgress />
      <SiteHeader
        variant="home"
        isAuthenticated={isAuthenticated}
        isAdmin={isAdmin}
        user={user}
        isEditMode={isEditMode}
        dirtyFields={dirtyFields}
        getContent={getContent}
        onContentSave={handleContentSave}
        onDirtyStateChange={handleDirtyStateChange}
        onEditModeToggle={handleEditModeToggle}
        onLogout={logout}
      />
      <ThermalStage stageRef={stageRef} screwRef={screwRef} getContent={getContent} />
      <main id="main" ref={mainRef}>
        <HeroSection {...sectionProps} />
        <ProductsIntroSection {...sectionProps} visualRef={visualRef} />
        <OrderingSection {...sectionProps} />
        <ContactSection {...sectionProps} />
      </main>
      <SectionDots />
    </div>
  );
};

const LoginPage: React.FC = () => {
  return <Login />;
};

const CallbackPage: React.FC = () => {
  const { user, isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    // Once authentication is complete, redirect to home
    if (isAuthenticated && user && !isLoading) {
      window.location.href = '/';
    }
  }, [isAuthenticated, user, isLoading]);

  return (
    <div className="login-container">
      <div className="login-form">
        <div className="auth-header">
          <img src="/astra_logo.png" alt="YH Logo" className="auth-logo" />
          <h3>盈紘有限公司帳號管理系統</h3>
        </div>
        <div className="loading">
          <p>{isLoading ? '處理登入中...' : '登入完成，正在重定向...'}</p>
        </div>
      </div>
    </div>
  );
};

const SilentCallbackPage: React.FC = () => {
  useEffect(() => {
    // For silent refresh, the OIDC library handles everything automatically
    // This page exists to fulfill the silent_redirect_uri requirement
  }, []);

  return <div>Silent callback processing...</div>;
};

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/callback" element={<CallbackPage />} />
          <Route path="/silent-callback" element={<SilentCallbackPage />} />
          <Route path="/products" element={<Products />} />
          <Route path="/" element={<MainContent />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
